import { CALORIE_DEVIATION_THRESHOLD } from "../model/constants";
import { NutritionDeclarationType } from "../model/nutritionPlanTypes";

/**
 * Intake-view calculations ported from the mobile `DayIntakeContainerWrapper`.
 * The API gives the day's current `declaration` and its `deviation` from the
 * original plan; the original is reconstructed as current − deviation.
 */

export type DeviationType = "increased" | "reduced" | "skipped";

export type DeviationInfo = {
  hasDeviation: boolean;
  type: DeviationType | null;
};

/** Field-by-field `current − extra`, over the union of both objects' keys. */
export const computeDifferences = (
  current: Partial<NutritionDeclarationType>,
  extra: Partial<NutritionDeclarationType>
): NutritionDeclarationType => {
  const result: Partial<NutritionDeclarationType> = {};
  const keys = new Set([...Object.keys(current), ...Object.keys(extra)]) as Set<
    keyof NutritionDeclarationType
  >;
  keys.forEach((key) => {
    result[key] = (current[key] ?? 0) - (extra[key] ?? 0);
  });
  return result as NutritionDeclarationType;
};

/** Daily view: only calorie deviations of 50 kcal or more are called out. */
export const getDailyDeviation = (
  deviation: Partial<NutritionDeclarationType> | undefined
): DeviationInfo => {
  const cals = deviation?.cals || 0;
  if (Math.abs(cals) < CALORIE_DEVIATION_THRESHOLD) {
    return { hasDeviation: false, type: null };
  }
  return { hasDeviation: true, type: cals > 0 ? "increased" : "reduced" };
};

/**
 * Meal view: zero current calories means the meal was skipped; otherwise any
 * non-zero difference from the original is reported.
 */
export const getMealDeviation = (
  currentCals: number,
  originalCals: number
): DeviationInfo => {
  if (currentCals === 0) return { hasDeviation: true, type: "skipped" };
  const diff = currentCals - originalCals;
  if (diff === 0) return { hasDeviation: false, type: null };
  return { hasDeviation: true, type: diff > 0 ? "increased" : "reduced" };
};

/** Copy for the deviation banner, shared by the daily and meal views. */
export const getDeviationMessage = (
  type: DeviationType
): { title: string; description: string } => {
  switch (type) {
    case "increased":
      return {
        title: "Increased intake",
        description:
          "Your intake is above target. This may be due to added items, larger portions, or replacing skipped meals with higher-calorie options.",
      };
    case "reduced":
      return {
        title: "Reduced intake",
        description:
          "Your intake is below target. You may have skipped items, reduced portions, or missed an entire meal.",
      };
    case "skipped":
    default:
      return {
        title: "Meal skipped",
        description:
          "You've skipped all items in this meal, resulting in no intake. Try not to miss entire meals to stay on track with your nutritional plan.",
      };
  }
};
